// src/pages/booking/BookingUpload.jsx
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  UploadCloud,
  CheckCircle,
  AlertCircle,
  FileX,
} from "lucide-react";
import api from "../../services/api";
import ErrorState from "../../components/ErrorState";
import "./booking-upload.scss";

export default function BookingUpload() {
  const { id } = useParams();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  /* ================= LOAD BOOKING ================= */
  useEffect(() => {
    async function load() {
      try {
        const res = await api.get(`/api/bookings/${id}`);
        setBooking(res.data);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            "Booking tidak ditemukan"
        );
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  /* ================= FILE HANDLER ================= */
  function handleFile(e) {
    const f = e.target.files[0];
    if (!f) return;

    if (!f.type.startsWith("image/")) {
      setError("File harus berupa gambar (JPG / PNG)");
      return;
    }

    if (f.size > 5 * 1024 * 1024) {
      setError("Ukuran maksimal file 5MB");
      return;
    }

    setError("");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  }

  /* ================= SUBMIT ================= */
  async function submit() {
    if (!file || submitting) return;

    setSubmitting(true);
    setError("");

    try {
      const form = new FormData();
      form.append("image", file);

      await api.post(
        `/api/bookings/${id}/payment-proof`,
        form
      );

      setSuccess(true);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Gagal upload bukti pembayaran"
      );
    } finally {
      setSubmitting(false);
    }
  }

  /* ================= STATES ================= */
  if (loading) {
    return (
      <div className="booking-upload-page booking-upload__state">
        Memuat data booking…
      </div>
    );
  }

  if (error && !booking) {
    return (
      <ErrorState
        icon={FileX}
        title="Booking Tidak Ditemukan"
        message={error || "Data booking tidak ditemukan."}
        action={{ label: "Kembali ke Beranda", href: "/" }}
      />
    );
  }

  /* ================= SUCCESS ================= */
  if (success) {
    return (
      <div className="booking-upload-page booking-upload__success">
        <div className="success-icon">
          <CheckCircle size={64} />
        </div>
        <h2>Bukti Pembayaran Terkirim</h2>
        <p>
          Terima kasih. Bukti pembayaran kamu sudah
          diterima dan sedang diverifikasi oleh panitia.
        </p>
        <span>
          Tiket & QR Code akan dikirim via email setelah
          verifikasi selesai.
        </span>
      </div>
    );
  }

  /* ================= MAIN ================= */
  return (
    <div className="booking-upload-page booking-upload">
      <div className="card">
        <h1>Upload Bukti Pembayaran</h1>

        {/* CUSTOMER INFO */}
        <div className="customer-info">
          <div>
            <span>Nama</span>
            <strong>{booking.customerSnapshot.name}</strong>
          </div>
          <div>
            <span>Email</span>
            <strong>{booking.customerSnapshot.email}</strong>
          </div>
          <div>
            <span>No. WhatsApp</span>
            <strong>{booking.customerSnapshot.phone}</strong>
          </div>
        </div>

        {/* BOOKING SUMMARY */}
        <div className="summary">
          <div>
            <span>Event</span>
            <strong>{booking.event.title}</strong>
          </div>
          <div>
            <span>Total</span>
            <strong>
              Rp{" "}
              {booking.ticket.total.toLocaleString("id-ID")}
            </strong>
          </div>
          <div>
            <span>Status</span>
            <strong className="status">
              Menunggu Pembayaran
            </strong>
          </div>
        </div>

        {/* ERROR */}
        {error && (
          <div className="error-box">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* UPLOAD */}
        <label className="upload-box">
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleFile}
          />

          {!preview && (
            <>
              <UploadCloud size={32} />
              <p>Klik atau drag bukti pembayaran</p>
              <span>JPG / PNG · Maks 5MB</span>
            </>
          )}

          {preview && (
            <div className="image-preview">
              <img src={preview} alt="Preview bukti bayar" />
            </div>
          )}
        </label>

        <button
          className="btn-primary"
          disabled={!file || submitting}
          onClick={submit}
        >
          {submitting ? "Mengirim..." : "Kirim Bukti Pembayaran"}
        </button>

        <p className="hint">
          Pembayaran diverifikasi maksimal 1×24 jam
        </p>
      </div>
    </div>
  );
}
